import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { theme } from '../../theme.js';
import { PageShell } from '../../components/PageShell.js';
import { Icon } from '../../components/Icon.js';
import { Button } from '../../components/Button.js';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../lib/api.js';
import { signOut } from '../../lib/auth.js';
import { useBack } from '../../lib/back.js';
import { useAuth } from '../../context/AuthContext.js';
import {
  useSystemOverview, useSystemOrg, useCreateSystemOrg, useUpdateSystemOrg, useAddOrgAdmin,
  type SystemOrgStats, type SystemInvite,
} from '../../lib/queries.js';

/**
 * System admin: the units on the service, how each is used, and who runs
 * them. Counts only — a unit's own records stay with its members.
 */
export function SystemDashboardPage({ standalone }: { standalone?: boolean }) {
  const nav = useNavigate();
  // Back pops to wherever System was opened from (Profile), rather than
  // pushing Profile again — that made Profile ⇄ System an endless loop.
  const back = useBack('/profile');
  const { refresh } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading } = useSystemOverview();
  const [adding, setAdding] = useState(false);
  const t = data?.totals;

  return (
    <SystemShell
      title="System"
      // In no unit, there's nothing to go back to — only signing out.
      {...(standalone
        ? { onSignOut: async () => { await signOut(); qc.clear(); await refresh(); nav('/login', { replace: true }); } }
        : { onBack: back })}
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
        <Tile label="Units" value={t?.orgs} />
        <Tile label="People" value={t?.people} sub={t && `${t.appUsers} use the app`} />
        <Tile label="Active, 7 days" value={t?.active7d} sub={t && `${t.active30d} in 30 days`} />
        <Tile label="Checks, 30 days" value={t?.checks30d} />
        <Tile label="Cover requests" value={t?.coverOpen} sub={t && `open · ${t.cover30d} in 30 days`} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', margin: '24px 0 10px' }}>
        <div style={{ flex: 1, fontSize: 15, fontWeight: 700, color: theme.text }}>Units</div>
        {!adding && <Button size="sm" icon="plus" onClick={() => setAdding(true)}>New unit</Button>}
      </div>

      {adding && <NewUnitForm onDone={() => setAdding(false)} />}

      {isLoading && <Muted>Loading…</Muted>}
      {data?.orgs.length === 0 && <Muted>No units yet.</Muted>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {data?.orgs.map((o) => <UnitCard key={o.id} org={o} onOpen={() => nav(`/system/orgs/${o.id}`)} />)}
      </div>
      <Muted>
        Active counts the people who opened the app in that unit. It started counting when this page was added.
      </Muted>
    </SystemShell>
  );
}

function UnitCard({ org: o, onOpen }: { org: SystemOrgStats; onOpen: () => void }) {
  return (
    <button onClick={onOpen} style={{ ...card, textAlign: 'left', cursor: 'pointer', width: '100%', fontFamily: theme.font }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1, minWidth: 0, fontSize: 16, fontWeight: 700, color: theme.text }}>{o.name}</div>
        <div style={{ fontSize: 12, color: theme.textMuted }}>{seen(o.lastSeenAt)}</div>
        <span style={{ color: theme.textMuted }}><Icon name="chevron" size={16} /></span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '8px 12px' }}>
        <Stat label="People" value={`${o.members}`} sub={`${o.appUsers} on app`} />
        <Stat label="Active 7d" value={`${o.active7d}`} sub={`${o.active30d} in 30d`} />
        <Stat label="Admins" value={`${o.admins}`} warn={o.admins === 0} />
        <Stat label="Checks 30d" value={`${o.checks30d}`} />
        <Stat label="Cover open" value={`${o.coverOpen}`} />
        <Stat label="Shifts 4 wks" value={`${o.shifts28d}`} />
      </div>
    </button>
  );
}

function NewUnitForm({ onDone }: { onDone: () => void }) {
  const create = useCreateSystemOrg();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');

  if (create.data) {
    return (
      <div style={{ ...card, marginBottom: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: theme.text, marginBottom: 6 }}>{create.data.org.name} created</div>
        <InviteLink invite={create.data.invite} />
        <div style={{ marginTop: 12 }}><Button size="sm" variant="secondary" onClick={onDone}>Done</Button></div>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); create.mutate({ name: name.trim(), slug, adminName: adminName.trim(), adminEmail: adminEmail.trim() }); }}
      style={{ ...card, display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}
    >
      <div style={{ fontSize: 15, fontWeight: 700, color: theme.text }}>New unit</div>
      <Field label="Name">
        <input value={name} required minLength={2} maxLength={100} style={input} placeholder="e.g. Monash SES"
          onChange={(e) => { setName(e.target.value); if (!slugEdited) setSlug(slugify(e.target.value)); }} />
      </Field>
      <Field label="Short name" hint="Lowercase letters, numbers and hyphens">
        <input value={slug} required minLength={2} maxLength={50} pattern="[a-z0-9-]+" style={input}
          onChange={(e) => { setSlug(e.target.value); setSlugEdited(true); }} />
      </Field>
      <Field label="First admin's name">
        <input value={adminName} required maxLength={100} style={input} onChange={(e) => setAdminName(e.target.value)} />
      </Field>
      <Field label="First admin's email" hint="They get an invite link to send on — valid for a week">
        <input value={adminEmail} required type="email" style={input} onChange={(e) => setAdminEmail(e.target.value)} />
      </Field>
      <Problem error={create.error} />
      <div style={{ display: 'flex', gap: 8 }}>
        <Button type="submit" size="sm" loading={create.isPending}>Create unit</Button>
        <Button size="sm" variant="secondary" onClick={onDone}>Cancel</Button>
      </div>
      <Muted left>New units start with the default qualifications, positions and Victorian public holidays.</Muted>
    </form>
  );
}

export function SystemOrgPage() {
  const back = useBack('/system');
  const { orgId = '' } = useParams<{ orgId: string }>();
  const { data, isLoading } = useSystemOrg(orgId);
  const o = data?.org;

  return (
    <SystemShell title={o?.name ?? 'Unit'} onBack={back}>
      {isLoading && <Muted>Loading…</Muted>}
      {o && (
        <>
          <div style={{ ...card, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '12px 14px' }}>
            <Stat label="People" value={`${o.members}`} sub={`${o.appUsers} on app`} />
            <Stat label="Active 7d" value={`${o.active7d}`} sub={`${o.active30d} in 30d`} />
            <Stat label="Notifications on" value={`${o.pushUsers}`} />
            <Stat label="Teams" value={`${o.teams}`} />
            <Stat label="Assets" value={`${o.assets}`} />
            <Stat label="Checks 30d" value={`${o.checks30d}`} />
            <Stat label="Cover open" value={`${o.coverOpen}`} sub={`${o.cover30d} in 30d`} />
            <Stat label="Shifts 4 wks" value={`${o.shifts28d}`} />
            <Stat label="Invites pending" value={`${o.pendingInvites}`} />
            <Stat label="Last change" value={seen(o.lastChangeAt)} />
            <Stat label="Created" value={new Date(o.createdAt).toLocaleDateString()} />
          </div>

          <Section title="Admins">
            {data.admins.length === 0 && <Muted left>No admins — add one below.</Muted>}
            {data.admins.map((a) => (
              <div key={a.userId} style={row}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>{a.displayName}</div>
                  <div style={{ fontSize: 12, color: theme.textMuted, overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.email ?? 'No email'}</div>
                </div>
                <div style={{ fontSize: 12, color: theme.textMuted, textAlign: 'right' }}>
                  {a.hasApp ? seen(a.lastSeenAt) : 'Not on the app yet'}
                </div>
              </div>
            ))}
            {data.adminInvites.map((i) => (
              <div key={i.id} style={row}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>{i.displayName}</div>
                  <div style={{ fontSize: 12, color: theme.textMuted }}>{i.email}</div>
                </div>
                <div style={{ fontSize: 12, color: theme.status.warn.fg }}>
                  Invited · until {new Date(i.expiresAt).toLocaleDateString()}
                </div>
              </div>
            ))}
            <AddAdminForm orgId={orgId} />
          </Section>

          <Section title="Name">
            <RenameForm orgId={orgId} name={o.name} slug={o.slug} />
          </Section>
        </>
      )}
    </SystemShell>
  );
}

function AddAdminForm({ orgId }: { orgId: string }) {
  const add = useAddOrgAdmin(orgId);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        add.mutate({ email: email.trim(), displayName: name.trim() }, { onSuccess: () => { setEmail(''); setName(''); } });
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, color: theme.textDim }}>Add an admin</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input value={name} required maxLength={100} placeholder="Name" aria-label="Admin name"
          style={{ ...input, flex: '1 1 160px' }} onChange={(e) => setName(e.target.value)} />
        <input value={email} required type="email" placeholder="Email" aria-label="Admin email"
          style={{ ...input, flex: '2 1 200px' }} onChange={(e) => setEmail(e.target.value)} />
        <Button type="submit" size="sm" loading={add.isPending}>Add</Button>
      </div>
      <Muted left>Someone already in the unit becomes an admin straight away. Anyone else gets an invite link to send on.</Muted>
      <Problem error={add.error} />
      {add.data && !add.data.invite && <Done>Made an admin. They'll be asked to set up two-factor sign-in next time.</Done>}
      {add.data?.invite && <InviteLink invite={add.data.invite} />}
    </form>
  );
}

function RenameForm({ orgId, name, slug }: { orgId: string; name: string; slug: string }) {
  const update = useUpdateSystemOrg(orgId);
  const [draftName, setDraftName] = useState(name);
  const [draftSlug, setDraftSlug] = useState(slug);
  const changed = draftName.trim() !== name || draftSlug !== slug;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        update.mutate({
          ...(draftName.trim() !== name ? { name: draftName.trim() } : {}),
          ...(draftSlug !== slug ? { slug: draftSlug } : {}),
        });
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      <Field label="Name">
        <input value={draftName} required minLength={2} maxLength={100} style={input} onChange={(e) => setDraftName(e.target.value)} />
      </Field>
      <Field label="Short name">
        <input value={draftSlug} required minLength={2} maxLength={50} pattern="[a-z0-9-]+" style={input}
          onChange={(e) => setDraftSlug(e.target.value)} />
      </Field>
      <Problem error={update.error} />
      {update.isSuccess && !changed && <Done>Saved.</Done>}
      <div><Button type="submit" size="sm" disabled={!changed} loading={update.isPending}>Save</Button></div>
    </form>
  );
}

function InviteLink({ invite }: { invite: SystemInvite }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try { await navigator.clipboard.writeText(invite.url); setCopied(true); } catch { /* select it by hand */ }
  }
  return (
    <div style={{ padding: 12, borderRadius: 10, background: theme.surface2, border: `1px solid ${theme.border}` }}>
      <div style={{ fontSize: 13, color: theme.text, marginBottom: 6 }}>
        Send this link to {invite.displayName} ({invite.email}). It works once, until {new Date(invite.expiresAt).toLocaleDateString()}.
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input readOnly value={invite.url} aria-label="Invite link" onFocus={(e) => e.target.select()}
          style={{ ...input, flex: 1, fontFamily: 'monospace', fontSize: 12 }} />
        <Button size="sm" variant="secondary" onClick={copy}>{copied ? 'Copied' : 'Copy'}</Button>
      </div>
    </div>
  );
}

// ── Bits ─────────────────────────────────────────────────────

function SystemShell({ title, onBack, onSignOut, children }: {
  title: string; onBack?: () => void; onSignOut?: () => void; children: React.ReactNode;
}) {
  return (
    <PageShell wide noNav>
      <div style={{
        padding: '14px 18px', background: theme.surface, borderBottom: `1px solid ${theme.border}`,
        display: 'flex', alignItems: 'center', gap: 10, position: 'sticky', top: 0, zIndex: 5, flexShrink: 0,
      }}>
        {onBack && <button onClick={onBack} aria-label="Back" style={iconBtn}><Icon name="back" size={18} /></button>}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: theme.textMuted, letterSpacing: 0.5 }}>SYSTEM ADMIN</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: theme.text, letterSpacing: -0.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
        </div>
        {onSignOut && <Button size="sm" variant="secondary" onClick={onSignOut}>Sign out</Button>}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 32px', background: theme.bg }}>{children}</div>
    </PageShell>
  );
}

function Tile({ label, value, sub }: { label: string; value: number | undefined; sub?: string | undefined }) {
  return (
    <div style={card}>
      <div style={{ fontSize: 12, color: theme.textMuted, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: theme.text, letterSpacing: -0.5, fontVariantNumeric: 'tabular-nums' }}>{value ?? '–'}</div>
      {sub && <div style={{ fontSize: 12, color: theme.textMuted }}>{sub}</div>}
    </div>
  );
}

function Stat({ label, value, sub, warn }: { label: string; value: string; sub?: string; warn?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: theme.textMuted, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: warn ? theme.status.danger.fg : theme.text, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: theme.textMuted }}>{sub}</div>}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ ...card, marginTop: 14 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: theme.text, marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: theme.textDim }}>{label}</span>
      {children}
      {hint && <span style={{ fontSize: 11, color: theme.textMuted }}>{hint}</span>}
    </label>
  );
}

function Problem({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <div style={{ fontSize: 13, color: theme.status.danger.fg, background: theme.status.danger.bg, padding: '8px 12px', borderRadius: 8 }}>
      {error instanceof ApiError ? error.message : 'Something went wrong'}
    </div>
  );
}

function Done({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 13, color: theme.status.ok.fg, background: theme.status.ok.bg, padding: '8px 12px', borderRadius: 8 }}>{children}</div>;
}

function Muted({ children, left }: { children: React.ReactNode; left?: boolean }) {
  return <div style={{ padding: left ? '2px 0' : '12px 0', fontSize: 12, color: theme.textMuted, textAlign: left ? 'left' : 'center' }}>{children}</div>;
}

/** "Today", "3 days ago", or "Never". */
function seen(at: string | null): string {
  if (!at) return 'Never';
  const days = Math.floor((Date.now() - new Date(at).getTime()) / 86400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 60) return `${days} days ago`;
  return new Date(at).toLocaleDateString();
}

function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 50);
}

const card: React.CSSProperties = {
  background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 14, padding: 14,
};

const row: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: `1px solid ${theme.border}`,
};

const input: React.CSSProperties = {
  padding: '9px 10px', background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 8,
  fontSize: 14, color: theme.text, fontFamily: theme.font, outline: 'none', boxSizing: 'border-box', minWidth: 0,
};

const iconBtn: React.CSSProperties = {
  width: 36, height: 36, border: `1px solid ${theme.border}`, flexShrink: 0,
  background: theme.surface2, color: theme.text, borderRadius: 10,
  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
};
